import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, TrendingDown, Calendar, Target, Zap, Clock, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { apiGet } from '../utils/apiRequest';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const DeepAnalysisResults = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalysis();
  }, [analysisId]);

  const fetchAnalysis = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      // Use apiGet which handles 401 and token refresh automatically
      const data = await apiGet(`/api/deep-analysis/${analysisId}`);
      setAnalysis(data);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError(err.message || 'Failed to load Deep Analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/individual" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg inline-block">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const deepAnalysis = analysis?.deep_analysis || {};
  const scoreInterpretation = deepAnalysis?.score_interpretation || {};
  const risks = deepAnalysis?.risks || [];
  const opportunities = deepAnalysis?.opportunities || [];
  const actions30Days = deepAnalysis?.actions_30_days || [];
  const actions90Days = deepAnalysis?.actions_90_days || [];
  const longTermActions = deepAnalysis?.long_term_actions || [];
  const serviceRec = deepAnalysis?.service_recommendation || {};

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'acceptable': return 'text-blue-600 bg-blue-100';
      case 'fragile': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Owner-friendly labels for risk categories. The backend keeps sending the
  // same category codes (unchanged risk classification) -- only the label
  // shown to the business owner is translated here.
  const riskCategoryLabels = {
    profitability: "You're losing money",
    cash_flow: 'Cash is getting tight',
    data_quality: "Your books can't fully be trusted yet",
    operations: 'Your financial processes are falling behind',
    compliance: 'Tax and payroll filings need attention',
    other: 'Other risk to know about',
  };
  const getRiskLabel = (category) => riskCategoryLabels[category] || 'Risk';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Finance Vitals</span>
            </Link>
            <Link to="/individual" className="text-blue-600 hover:text-blue-800">
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Deep Financial Analysis</h1>
          <p className="text-gray-600">Powered by AI • Generated on {new Date(analysis.created_at).toLocaleDateString()}</p>
        </div>

        {/* Admin Narrative */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Executive Summary</h2>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{analysis.admin_narrative}</p>
        </div>

        {/* Score Interpretation */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Score Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Score</div>
              <div className="text-2xl font-bold text-gray-900">{scoreInterpretation.total_score}/100</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Basics</div>
              <div className="text-2xl font-bold text-gray-900">{scoreInterpretation.basics_score}/35</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Business</div>
              <div className="text-2xl font-bold text-gray-900">{scoreInterpretation.business_score}/35</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Checks</div>
              <div className="text-2xl font-bold text-gray-900">{scoreInterpretation.checks_score}/30</div>
            </div>
          </div>
          <div className={`inline-block px-4 py-2 rounded-full font-semibold ${getStatusColor(scoreInterpretation.status)}`}>
            Status: {scoreInterpretation.status?.toUpperCase()}
          </div>
          {scoreInterpretation.main_drivers && scoreInterpretation.main_drivers.length > 0 && (
            <div className="mt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Main Drivers:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {scoreInterpretation.main_drivers.map((driver, idx) => (
                  <li key={idx}>{driver}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Risks */}
        {risks.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              Identified Risks
            </h2>
            <div className="space-y-4">
              {risks.map((risk, idx) => (
                <div key={idx} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-semibold text-gray-900" data-testid={`risk-category-${idx}`}>{getRiskLabel(risk.category)}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(risk.severity)}`}>
                      {risk.severity?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-gray-700">{risk.description}</p>
                  {risk.inferred && (
                    <p className="text-xs text-gray-500 mt-2 italic">* This is our best interpretation based on the available data, not a certainty -- worth double-checking against your specific situation.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Opportunities */}
        {opportunities.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Zap className="w-6 h-6 text-green-600 mr-2" />
              Opportunities for Improvement
            </h2>
            <div className="space-y-4">
              {opportunities.map((opp, idx) => (
                <div key={idx} className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(opp.expected_impact)}`}>
                      {opp.expected_impact?.toUpperCase()} IMPACT
                    </span>
                    <span className="text-xs text-gray-600 capitalize">{opp.timeframe?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-gray-700">{opp.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* 30 Days */}
          {actions30Days.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Target className="w-5 h-5 text-red-600 mr-2" />
                Next 30 Days
              </h3>
              <div className="space-y-3">
                {actions30Days.map((action, idx) => (
                  <div key={idx} className="bg-red-50 rounded-lg p-3">
                    <div className="flex items-start">
                      <ChevronRight className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{action.title}</h4>
                        <p className="text-xs text-gray-600 mt-1">{action.goal}</p>
                        {action.priority === 'must_do' && (
                          <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                            MUST DO
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 90 Days */}
          {actions90Days.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 text-yellow-600 mr-2" />
                Next 90 Days
              </h3>
              <div className="space-y-3">
                {actions90Days.map((action, idx) => (
                  <div key={idx} className="bg-yellow-50 rounded-lg p-3">
                    <div className="flex items-start">
                      <ChevronRight className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{action.title}</h4>
                        <p className="text-xs text-gray-600 mt-1">{action.goal}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Long Term */}
          {longTermActions.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 text-blue-600 mr-2" />
                Long Term
              </h3>
              <div className="space-y-3">
                {longTermActions.map((action, idx) => (
                  <div key={idx} className="bg-blue-50 rounded-lg p-3">
                    <div className="flex items-start">
                      <ChevronRight className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{action.title}</h4>
                        <p className="text-xs text-gray-600 mt-1">{action.goal}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Service Recommendation */}
        {serviceRec && (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-sm border border-purple-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recommended Next Steps</h2>
            <div className="space-y-3 mb-4">
              {serviceRec.needs_cleanup && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                  <span className="text-gray-700">One-time bookkeeping cleanup recommended</span>
                </div>
              )}
              {serviceRec.needs_ongoing_bookkeeping && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                  <span className="text-gray-700">Ongoing monthly bookkeeping services</span>
                </div>
              )}
              {serviceRec.needs_cfo_or_advisory && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                  <span className="text-gray-700">CFO or financial advisory services</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 bg-white rounded-lg p-4">
              <strong>Recommendation:</strong> {serviceRec.justification}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepAnalysisResults;
